import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeftRight,
  Copy,
  Check,
  Play,
  RefreshCw,
  Trash2,
  Webhook,
  Wrench,
  Zap,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JsonViewer } from "@/components/common/JsonViewer";
import { N8nVmActionsPanel } from "./N8nVmActionsPanel";
import { N8nVmDeletePanel } from "./N8nVmDeletePanel";
import {
  useResourceN8nEndpoints,
  useResourceN8nProbe,
} from "@/hooks/useResourceQueries";
import { cn, formatDateTime } from "@/lib/utils";
import type { N8nEndpoint, N8nProbeResult } from "@/types/admin";

interface N8nProbePanelProps {
  resourceId: string;
  resourceName: string;
  vmUuid?: string | null;
}

/**
 * Sidebar key for the write half of the tab. It is not an endpoint key — the backend whitelist only
 * describes the read-only probes — so it is kept out of that namespace by the `__` prefix.
 */
const ACTIONS_KEY = "__vm-actions";
const DELETE_KEY = "__vm-delete";

/** Unwraps the single-element array some n8n webhooks wrap their envelope in. */
function unwrapEnvelope(response: unknown): Record<string, unknown> | null {
  const candidate = Array.isArray(response) ? response[0] : response;
  return candidate && typeof candidate === "object" && !Array.isArray(candidate)
    ? (candidate as Record<string, unknown>)
    : null;
}

/** Reads the envelope's `data` object, where every VM webhook puts its payload. */
function envelopeData(response: unknown): Record<string, unknown> | null {
  const envelope = unwrapEnvelope(response);
  const data = envelope?.data;
  return data && typeof data === "object" && !Array.isArray(data)
    ? (data as Record<string, unknown>)
    : null;
}

function asText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value || null;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return null;
}

/** Reads a nested value out of an untyped object, tolerating every missing level. */
function pick(source: unknown, ...path: string[]): unknown {
  let current = source;
  for (const segment of path) {
    if (!current || typeof current !== "object" || Array.isArray(current))
      return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/**
 * The handful of fields where WiCloud's row and the provider's answer are directly comparable, for
 * the probes that return them. Anything richer is left to the two JSON blocks — a mapping layer
 * that guessed at more would be the very thing this tab exists to avoid.
 *
 * The WiCloud side of the hardware rows comes from stored metadata, which is what the provider
 * reported when the VM was provisioned — so a mismatch there is real drift, not a unit difference.
 */
function buildComparison(
  probe: N8nProbeResult,
): { label: string; local: string | null; provider: string | null }[] {
  const data = envelopeData(probe.response);
  if (!data) return [];

  const metadata = probe.local.metadata;
  const rows: {
    label: string;
    local: string | null;
    provider: string | null;
  }[] = [];

  const push = (label: string, local: unknown, provider: unknown) => {
    const localText = asText(local);
    const providerText = asText(provider);
    if (localText || providerText) {
      rows.push({ label, local: localText, provider: providerText });
    }
  };

  const providerStatus = asText(data.status);
  const providerQmpStatus = asText(data.qmpstatus);
  if (providerStatus || providerQmpStatus) {
    push(
      "Power state",
      probe.local.status,
      [providerStatus, providerQmpStatus].filter(Boolean).join(" / "),
    );
  }

  push("VM UUID", probe.local.vmUuid, data.uuid);
  push("vCPU", pick(metadata, "cores"), data.cores);
  push("Memory (MB)", pick(metadata, "memory"), data.memory);

  // Three spellings for the same field: vm-status says "network", the network probe says
  // "networks", and cloud-init says "networks[].ip_address".
  const providerNet0 =
    pick(data, "network", "net0", "ip") ??
    pick(data, "networks", "net0", "ip") ??
    pick(data, "networks", "net0", "ip_address");
  push("net0 IP", pick(metadata, "network", "net0", "ip"), providerNet0);

  push(
    "scsi0 size",
    pick(metadata, "disk", "scsi0", "size"),
    pick(data, "disk", "scsi0", "size"),
  );

  return rows;
}

function statusBadgeVariant(
  status: number | null,
): "default" | "secondary" | "destructive" | "warning" {
  if (status === null) return "secondary";
  if (status >= 500) return "destructive";
  if (status >= 400) return "warning";
  if (status >= 200 && status < 300) return "default";
  return "secondary";
}

/**
 * n8n / WiService diagnostics for one resource: runs the read-only provider webhooks and shows the
 * response verbatim next to what WiCloud has stored, so a support admin can see both sides of the
 * same VM without shell access to the provider.
 *
 * The endpoint list comes from the API (`GET resources/n8n/endpoints`), not from a copy kept here —
 * the backend whitelist is the single source of truth for what may be called.
 *
 * Probes marked `hasSideEffect` (the console ticket) are never run on selection; they wait for an
 * explicit click, because fetching one mints a real ticket on the provider.
 *
 * The last entry in the same list is the write half — power state, vCPU/memory and growing a disk.
 * It lives in this menu rather than below the probes so that both halves of the tab are reached the
 * same way, and selecting it calls nothing: every action inside waits for its own confirmation.
 */
export function N8nProbePanel({
  resourceId,
  resourceName,
  vmUuid,
}: N8nProbePanelProps) {
  const {
    data: endpoints,
    isLoading: isLoadingEndpoints,
    isFetching: isFetchingEndpoints,
    error: endpointsError,
    refetch: refetchEndpoints,
  } = useResourceN8nEndpoints();

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<string>("hour");
  // Side-effect probes stay disarmed until the operator clicks Run; the flag is per key so
  // switching away and back does not silently re-fire one.
  const [armedKeys, setArmedKeys] = useState<Record<string, boolean>>({});
  const [copiedPath, setCopiedPath] = useState(false);

  useEffect(() => {
    if (!selectedKey && endpoints && endpoints.length > 0) {
      setSelectedKey(endpoints[0].key);
    }
  }, [endpoints, selectedKey]);

  const selected: N8nEndpoint | undefined = useMemo(
    () => endpoints?.find((endpoint) => endpoint.key === selectedKey),
    [endpoints, selectedKey],
  );

  const isActionsSelected = selectedKey === ACTIONS_KEY;
  const isDeleteSelected = selectedKey === DELETE_KEY;
  const isArmed = !selected?.hasSideEffect || !!armedKeys[selected.key];

  const {
    data: probe,
    isFetching,
    error: probeError,
    refetch,
  } = useResourceN8nProbe(
    resourceId,
    isActionsSelected || isDeleteSelected ? null : selectedKey,
    selected?.supportsTimeframe ? timeframe : undefined,
    isArmed,
  );

  const groups = useMemo(() => {
    const vm = endpoints?.filter((endpoint) => endpoint.group === "vm") ?? [];
    const global =
      endpoints?.filter((endpoint) => endpoint.group === "global") ?? [];
    return [
      { title: "Per VM (by UUID)", items: vm },
      { title: "Provider-wide", items: global },
    ].filter((group) => group.items.length > 0);
  }, [endpoints]);

  const comparison = probe ? buildComparison(probe) : [];

  const handleCopyPath = async () => {
    if (!probe?.path) return;
    try {
      await navigator.clipboard.writeText(probe.path);
      setCopiedPath(true);
      window.setTimeout(() => setCopiedPath(false), 1500);
    } catch {
      // Clipboard unavailable — the path is selectable in the UI.
    }
  };

  if (endpointsError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Cannot load the probe catalog</AlertTitle>
        <AlertDescription>
          {endpointsError instanceof Error
            ? endpointsError.message
            : "Failed to load the list of n8n endpoints."}
          {/* The catalog is cached for the session (`staleTime: Infinity`), so without this a
              single failed read hid every probe — and the write actions behind them — until a
              full page reload. */}
          <div className="mt-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void refetchEndpoints()}
              disabled={isFetchingEndpoints}
            >
              {isFetchingEndpoints && <Spinner className="mr-2" />}
              Retry
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(230px,280px)_1fr] xl:items-start">
      {/* Endpoint list — served by the backend whitelist */}
      <Card className="border-border/80 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Webhook className="h-4 w-4 shrink-0" />
            n8n endpoints
          </CardTitle>
          <CardDescription>
            Read-only webhooks, called live on each selection, and direct VM
            actions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingEndpoints ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-9 w-full" />
              ))}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.title} className="space-y-1.5">
                <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {group.title}
                </p>
                {group.items.map((endpoint) => (
                  <button
                    key={endpoint.key}
                    type="button"
                    onClick={() => setSelectedKey(endpoint.key)}
                    className={cn(
                      "flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-sm transition-colors",
                      endpoint.key === selectedKey
                        ? "border-primary/40 bg-primary/10 font-medium text-foreground"
                        : "border-transparent text-muted-foreground hover:border-border/80 hover:bg-muted/50 hover:text-foreground",
                    )}
                  >
                    <span className="min-w-0 truncate">{endpoint.label}</span>
                    {endpoint.hasSideEffect && (
                      <Zap
                        className="h-3.5 w-3.5 shrink-0 text-amber-500"
                        aria-label="Has a provider-side effect"
                      />
                    )}
                  </button>
                ))}
              </div>
            ))
          )}

          {/* The write half. Not served by the endpoint catalog — those are the read-only probes —
              so it is listed here, last, after the operator has scrolled past every way of reading
              the machine before finding the ways of changing it. */}
          <div className="space-y-1.5">
            <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Upgrade (changes the VM)
            </p>
            <button
              type="button"
              onClick={() => setSelectedKey(ACTIONS_KEY)}
              className={cn(
                "flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-sm transition-colors",
                isActionsSelected
                  ? "border-destructive/40 bg-destructive-soft font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:border-border/80 hover:bg-muted/50 hover:text-foreground",
              )}
            >
              <span className="min-w-0 truncate">Power, vCPU/memory, disk</span>
              <Wrench
                className="h-3.5 w-3.5 shrink-0 text-destructive"
                aria-label="Writes to the machine"
              />
            </button>
          </div>
          <div className="space-y-1.5">
            <p className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Delete (removes the VM)
            </p>
            <button
              type="button"
              onClick={() => setSelectedKey(DELETE_KEY)}
              className={cn(
                "flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left text-sm transition-colors",
                isDeleteSelected
                  ? "border-destructive/40 bg-destructive-soft font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:border-border/80 hover:bg-muted/50 hover:text-foreground",
              )}
            >
              <span>Delete VM</span>
              <Trash2 className="h-3.5 w-3.5 shrink-0 text-destructive" />
            </button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        {isDeleteSelected ? (
          <N8nVmDeletePanel
            key={resourceId}
            resourceId={resourceId}
            resourceName={resourceName}
            vmUuid={vmUuid}
          />
        ) : isActionsSelected ? (
          <N8nVmActionsPanel
            resourceId={resourceId}
            resourceName={resourceName}
          />
        ) : (
          <>
            {/* Request — the outgoing half of the round trip */}
            <Card className="border-border/80 shadow-none">
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 space-y-1">
                    <CardTitle className="text-base">
                      {selected?.label ?? "Select an endpoint"}
                    </CardTitle>
                    <CardDescription>{selected?.description}</CardDescription>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {selected?.supportsTimeframe && (
                      <Select value={timeframe} onValueChange={setTimeframe}>
                        <SelectTrigger className="h-9 w-[120px]">
                          <SelectValue placeholder="Timeframe" />
                        </SelectTrigger>
                        <SelectContent>
                          {selected.timeframes.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!selected || isFetching}
                      onClick={() => {
                        if (!selected) return;
                        if (!isArmed) {
                          setArmedKeys((current) => ({
                            ...current,
                            [selected.key]: true,
                          }));
                          return;
                        }
                        void refetch();
                      }}
                      className="gap-1.5"
                    >
                      {isFetching ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : isArmed ? (
                        <RefreshCw className="h-3.5 w-3.5" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      {isFetching ? "Calling" : isArmed ? "Re-run" : "Run"}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/80 bg-muted/30 px-3 py-2">
                  <Badge variant="secondary" className="font-mono text-[11px]">
                    {selected?.method ?? "GET"}
                  </Badge>
                  <code className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
                    {probe?.path ?? selected?.pathTemplate ?? "—"}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyPath}
                    disabled={!probe?.path}
                    className="h-7 gap-1.5 px-2 text-xs"
                  >
                    {copiedPath ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    Path
                  </Button>
                </div>

                {selected?.hasSideEffect && !isArmed && (
                  <Alert className="border-amber-500/40 bg-amber-500/10 [&>svg]:text-amber-500">
                    <Zap className="h-4 w-4" />
                    <AlertTitle>Not called automatically</AlertTitle>
                    <AlertDescription>
                      This webhook changes provider-side state (it mints a
                      console ticket), so it only runs when you click Run.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Response — verbatim */}
            <Card className="border-border/80 shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Provider response</CardTitle>
                <CardDescription>
                  The webhook body exactly as WiService returned it — no field
                  dropped, no key renamed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {probeError ? (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Probe failed</AlertTitle>
                    <AlertDescription>
                      {probeError instanceof Error
                        ? probeError.message
                        : "The admin API could not run this probe."}
                    </AlertDescription>
                  </Alert>
                ) : !probe && isFetching ? (
                  <Skeleton className="h-56 w-full" />
                ) : !probe ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    {selected?.hasSideEffect
                      ? "Click Run to call this webhook."
                      : "Select an endpoint to call it."}
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={statusBadgeVariant(probe.httpStatusCode)}>
                        {probe.httpStatusCode === null
                          ? "No response"
                          : `HTTP ${probe.httpStatusCode}`}
                      </Badge>
                      {probe.providerSuccess !== null && (
                        <Badge
                          variant={
                            probe.providerSuccess ? "default" : "destructive"
                          }
                        >
                          success: {String(probe.providerSuccess)}
                        </Badge>
                      )}
                      {probe.providerSuccess === null &&
                        probe.httpStatusCode !== null && (
                          <Badge variant="secondary">no success field</Badge>
                        )}
                      <Badge
                        variant="outline"
                        className="font-mono text-[11px]"
                      >
                        {probe.durationMs} ms
                      </Badge>
                      {probe.timeframe && (
                        <Badge variant="outline">
                          timeframe: {probe.timeframe}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(probe.requestedAt)}
                      </span>
                      {isFetching && <Spinner className="h-3.5 w-3.5" />}
                    </div>

                    {probe.providerMessage && (
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">
                          message:
                        </span>{" "}
                        {probe.providerMessage}
                      </p>
                    )}

                    {probe.transportError && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>
                          {probe.httpStatusCode === null
                            ? "Nothing was returned"
                            : "Transport error"}
                        </AlertTitle>
                        <AlertDescription>
                          {probe.transportError}
                        </AlertDescription>
                      </Alert>
                    )}

                    {probe.response !== null && probe.response !== undefined ? (
                      <JsonViewer value={probe.response} />
                    ) : (
                      <JsonViewer
                        value={probe.responseText}
                        emptyLabel="Empty body"
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Both sides of the same VM */}
            {probe && (
              <Card className="border-border/80 shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <ArrowLeftRight className="h-4 w-4 shrink-0" />
                    WiCloud stored state
                  </CardTitle>
                  <CardDescription>
                    What this platform believes about the same resource. A
                    disagreement with the response above is the finding.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {comparison.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-border/80">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">
                              Field
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              WiCloud
                            </th>
                            <th className="px-3 py-2 text-left font-medium">
                              Provider
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparison.map((row) => (
                            <tr
                              key={row.label}
                              className="border-t border-border/80"
                            >
                              <td className="px-3 py-2 font-medium">
                                {row.label}
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">
                                {row.local ?? "—"}
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">
                                {row.provider ?? "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Row
                      </p>
                      <JsonViewer
                        value={{
                          resourceId: probe.local.resourceId,
                          tenantId: probe.local.tenantId,
                          name: probe.local.name,
                          type: probe.local.type,
                          status: probe.local.status,
                          providerType: probe.local.providerType,
                          providerResourceId: probe.local.providerResourceId,
                          region: probe.local.region,
                          vmUuid: probe.local.vmUuid,
                          provisionedAt: probe.local.provisionedAt,
                          suspendedAt: probe.local.suspendedAt,
                          expiresAt: probe.local.expiresAt,
                          terminatedAt: probe.local.terminatedAt,
                          createdAt: probe.local.createdAt,
                          updatedAt: probe.local.updatedAt,
                        }}
                        maxHeightClassName="max-h-72"
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Metadata (credentials redacted)
                      </p>
                      <JsonViewer
                        value={probe.local.metadata}
                        emptyLabel="No metadata stored"
                        maxHeightClassName="max-h-72"
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Specifications
                      </p>
                      <JsonViewer
                        value={probe.local.specifications}
                        emptyLabel="No specifications stored"
                        maxHeightClassName="max-h-72"
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Configuration
                      </p>
                      <JsonViewer
                        value={probe.local.configuration}
                        emptyLabel="No configuration stored"
                        maxHeightClassName="max-h-72"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
